// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/components/Header.js

import React, { useState, useEffect } from 'react';
import {
  FiLogIn as LogIn,
  FiMenu as Menu,
  FiX as Close
} from 'react-icons/fi';
import buildResolvedFile from '../utils/buildResolvedFile';
import LoginModal from '../../LoginModal';
import useBrandColors from '../utils/useBrandColors';

const Header = ({
  branding = {},
  subdomain = '',
  navItems = [],
  activeSection: controlledActiveSection = undefined,
  onNavClick = () => {},
  onActiveSectionChange = () => {},
  apiBaseURL = '',
  handleSignIn,
  email: propEmail = '',
  setEmail: propSetEmail = () => {},
  password: propPassword = '',
  setPassword: propSetPassword = () => {},
  loginError: propLoginError = ''
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();
  
  // Internal active section state
  const [internalActive, setInternalActive] = useState(
    controlledActiveSection || (navItems[0] && navItems[0].id) || 'hero'
  );

  // Local login state
  const [localEmail, setLocalEmail] = useState('');
  const [localPassword, setLocalPassword] = useState('');
  const [localLoginError, setLocalLoginError] = useState('');

  // Sync controlled prop
  useEffect(() => {
    if (typeof controlledActiveSection !== 'undefined' && controlledActiveSection !== internalActive) {
      setInternalActive(controlledActiveSection);
    }
  }, [controlledActiveSection, internalActive]);

  const activeSection = typeof controlledActiveSection !== 'undefined' ? controlledActiveSection : internalActive;

  // Scroll handler
  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Logo
  const logoSrc = buildResolvedFile(branding.logo, apiBaseURL).resolvedSrc || '';

  // Sign in handler
  const submitSignIn = async (e) => {
    if (typeof handleSignIn === 'function') {
      return handleSignIn(e);
    }

    e && e.preventDefault && e.preventDefault();
    setLocalLoginError('');
    if (!localEmail || !localPassword) {
      setLocalLoginError('Please enter email and password.');
      return;
    }
    console.log('Local sign in', { localEmail });
    setShowLogin(false);
  };

  // Navigation click
  const handleNavClick = (id) => {
    if (typeof controlledActiveSection === 'undefined') {
      setInternalActive(id);
    }
    if (typeof onNavClick === 'function') onNavClick(id);
    setIsMobileMenuOpen(false);
  };

  // Active section detection on scroll
  useEffect(() => {
    if (!Array.isArray(navItems) || navItems.length === 0) return;

    const ids = navItems.map(i => i.id).filter(Boolean);
    if (ids.length === 0) return;

    const computeActive = () => {
      if (showLogin) return;

      const vh = window.innerHeight || document.documentElement.clientHeight;
      const center = vh / 2;
      let bestId = null;
      let bestDist = Infinity;

      ids.forEach(id => {
        const el = document.getElementById(id);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const elCenter = rect.top + rect.height / 2;
        const dist = Math.abs(elCenter - center);
        if ((rect.bottom >= 0 && rect.top <= vh) && dist < bestDist) {
          bestDist = dist;
          bestId = id;
        } else if (!bestId && dist < bestDist) {
          bestDist = dist;
          bestId = id;
        }
      });

      if (bestId && bestId !== activeSection) {
        if (typeof controlledActiveSection === 'undefined') {
          setInternalActive(bestId);
        }
        try {
          onActiveSectionChange(bestId);
        } catch (err) {
          // ignore
        }
      }
    };

    computeActive();

    const onScroll = () => computeActive();
    const onResize = () => computeActive();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    const mo = new MutationObserver(() => {
      if (!showLogin) computeActive();
    });
    
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      mo.disconnect();
    };
  }, [navItems, controlledActiveSection, activeSection, onActiveSectionChange, showLogin]);

  const displayTitle = branding.title || subdomain || '';

  return (
    <>
      <header
        className={`
          fixed w-full top-0 z-50 transition-all duration-500 ease-smooth
          ${isScrolled
            ? 'bg-white/98 backdrop-blur-xl shadow-sm border-b py-2'
            : 'bg-white/95 backdrop-blur-sm py-3 border-b'
          }
        `}
        style={{ 
          borderBottomColor: isScrolled ? `${primary}10` : `${primary}08`,
          boxShadow: isScrolled ? `0 1px 3px ${primary}10` : 'none',
        }}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Brand - CHANGED: Removed flex-shrink-0, added flex-1 and mr-4 to protect the actions layout */}
            <div className="flex items-center space-x-3 min-w-0 flex-1 mr-4">
              {logoSrc && (
                <div className="flex-shrink-0 transition-all duration-500">
                  <img
                    src={logoSrc}
                    alt={branding.title || subdomain}
                    className={`
                      object-contain transition-all duration-500
                      ${isScrolled ? 'h-8 w-auto' : 'h-10 w-auto'}
                    `}
                  />
                </div>
              )}
              <div className="min-w-0 flex-1">
                {/* h1 - CHANGED: Added 'truncate' class to safely clip extreme lengths with an ellipsis (...) */}
                <h1 
                  className={`
                    font-bold tracking-tight transition-all duration-500 truncate
                    ${isScrolled ? 'text-base' : 'text-lg'}
                  `}
                  style={{ 
                    color: primary,
                    fontFamily: fontHeading,
                  }}
                  title={displayTitle}
                >
                  {displayTitle}
                </h1>
                {branding.slogan && !isScrolled && (
                  <p 
                    className="text-[0.55rem] font-light tracking-[0.15em] uppercase truncate"
                    style={{ color: secondary }}
                  >
                    {branding.slogan}
                  </p>
                )}
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              <button
                onClick={() => setShowLogin(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:shadow-lg whitespace-nowrap"
                style={{ 
                  backgroundColor: primary,
                  color: '#ffffff',
                  fontFamily: fontBody,
                }}
              >
                <LogIn className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Login</span>
              </button>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg transition-colors flex-shrink-0"
                style={{ 
                  color: primary,
                  backgroundColor: isMobileMenuOpen ? `${primary}10` : 'transparent',
                }}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <Close className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile/Desktop Navigation */}
          <div className={`
            overflow-hidden transition-all duration-300 ease-smooth
            ${isMobileMenuOpen ? 'max-h-[80vh] opacity-100 mt-3' : 'max-h-0 opacity-0'}
          `}>
            <div 
              className="py-3 border-t space-y-1 overflow-y-auto max-h-[70vh]"
              style={{ borderTopColor: `${primary}10` }}
            >
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`
                    w-full text-left px-4 py-2.5 rounded-lg transition-all duration-300 text-sm
                    ${activeSection === item.id
                      ? 'font-medium'
                      : ''
                    }
                  `}
                  style={{
                    backgroundColor: activeSection === item.id ? `${primary}10` : 'transparent',
                    color: activeSection === item.id ? primary : '#6B7280',
                    fontFamily: fontBody,
                  }}
                  type="button"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Login Modal */}
      {showLogin && (
        <LoginModal
          onClose={() => {
            setShowLogin(false);
            setLocalPassword('');
          }}
          handleSignIn={submitSignIn}
          email={typeof propSetEmail === 'function' ? propEmail : localEmail}
          setEmail={typeof propSetEmail === 'function' ? propSetEmail : setLocalEmail}
          password={typeof propSetPassword === 'function' ? propPassword : localPassword}
          setPassword={typeof propSetPassword === 'function' ? propSetPassword : setLocalPassword}
          loginError={typeof propSetEmail === 'function' ? propLoginError : localLoginError}
        />
      )}
    </>
  );
};

export default Header;